import {
    Links,
    Meta,
    Outlet,
    Scripts,
    useRouteError,
    ScrollRestoration,
    isRouteErrorResponse,
    useRouteLoaderData,
    LinksFunction,
    data,
    HeadersArgs,
} from "react-router";
import * as Sentry from "@sentry/react";
import stylesheet from "~/tailwind.css?url";
import "react-image-crop/dist/ReactCrop.css";
import { clientEnv } from "~/env";
import KetchPlugin from "~/components/atoms/KetchPlugin";
import { GoogleTagManagerFallback, useGoogleTagManager } from "~/components/atoms/GoogleTagManager";
import { featuresSession } from "~/lib/features.server";
import PageContainer from "~/components/templates/PageContainer";
import { toTitleCase } from "~/lib/format";
import { ErrorContent } from "~/components/atoms/ErrorContent";
import favicon from "~/assets/favicon.ico";
import { csrf } from "~/lib/csrf.server";
import { ClientOnly } from "remix-utils/client-only";
import { combineHeaders, useSavedScollPosition } from "~/lib/utils";
import { AuthenticityTokenProvider } from "remix-utils/csrf/react";
import { useDatadogRUM } from "~/lib/datadog";
import { messageSession } from "~/lib/sessions.server";
import type { Route } from "./+types/root";
import Toaster from "~/components/molecules/Toaster";
import { ModalProvider } from "~/components/providers/ModalProvider";
import { useToastMessages } from "~/business/toast";
import Tracker from "~/components/organisms/Tracker.client";

export const links: LinksFunction = () => [
    { rel: "preconnect", href: "https://use.typekit.net" },
    { rel: "preconnect", href: "https://p.typekit.net" },
    { rel: "preconnect", href: "https://global.ketchcdn.com" },
    { rel: "preload", as: "style", href: "https://use.typekit.net/kpm5ixy.css" },
    { rel: "stylesheet", href: stylesheet },
    { rel: "stylesheet", href: "https://use.typekit.net/kpm5ixy.css" },
    { rel: "icon", href: favicon, type: "image/x-icon" },
];
export const headers = ({ loaderHeaders }: HeadersArgs) => loaderHeaders;

export async function loader({ request }: Pick<Route.LoaderArgs, "request">) {
    const { get: getFeatureSession, headers: featureHeaders } = await featuresSession(request);
    const features = await getFeatureSession();
    const [csrfToken, csrfCookieHeader] = await csrf.commitToken();
    const { messages = [], headers: messageHeaders } = await messageSession(request).get();

    const rootData = {
        CLIENT_ENV: clientEnv,
        csrfToken,
        FEATURES: features,
        messages,
    };

    const headers = combineHeaders(
        messageHeaders,
        await featureHeaders(),
        csrfCookieHeader ? { "set-cookie": csrfCookieHeader } : null,
        { "Cache-Control": "no-store" },
    );
    return data(rootData, { headers });
}

function errorMeta(error: Route.MetaFunction["arguments"]["error"]): {
    title: string;
    description: string;
} {
    const defaultValues = {
        title: "OOPS! SOMETHING WENT WRONG.",
        description: "There was an error processing your request.",
    };
    if (isRouteErrorResponse(error) && error.status === 404) {
        return {
            title: "OOPS! PAGE NOT FOUND.",
            description: "The page you are looking for cannot be found.",
        };
    }
    return defaultValues;
}

export const meta: Route.MetaFunction = ({ error }) => {
    if (error) {
        const { title, description } = errorMeta(error);
        return [
            { title: toTitleCase(title) },
            {
                name: "description",
                content: description,
            },
        ];
    }
    return [
        {
            title: "NCSA | Get Recruited",
        },
    ];
};

function useRootData(): Route.ComponentProps["loaderData"] | undefined {
    const data = useRouteLoaderData<typeof loader>("root");
    const error = useRouteError();
    if (data) {
        return data;
    } else if (isRouteErrorResponse(error)) {
        // If we're here it means the root loader threw AND our error is a Response.
        // This is enough to guarantee we've got data in the shape of our root loader.
        return error.data as Route.ComponentProps["loaderData"];
    }
    return undefined;
}

export function Layout({ children }: { children: React.ReactNode }) {
    const data = useRootData();
    const GTM_ID = data?.CLIENT_ENV.GTM_ID || "";
    const GTM_ENABLED = !!data?.CLIENT_ENV.GTM_ENABLED && !!GTM_ID;
    useGoogleTagManager(GTM_ENABLED, GTM_ID);
    useDatadogRUM({
        applicationId: data?.CLIENT_ENV.DD_RUM_APP_ID,
        clientToken: data?.CLIENT_ENV.DD_RUM_CLIENT_TOKEN,
        service: "titan-web-client",
        env: data?.CLIENT_ENV.APP_ENVIRONMENT,
        version: data?.CLIENT_ENV.DD_VERSION,
    });
    useToastMessages(data?.messages ?? []);
    useSavedScollPosition();

    return (
        <AuthenticityTokenProvider token={data?.csrfToken ?? ""}>
            <html lang="en">
                <head>
                    <meta charSet="utf-8" />
                    <meta name="viewport" content="width=device-width, initial-scale=1" />
                    <Meta />
                    <Links />
                    <KetchPlugin />
                </head>
                <body className="relative">
                    <script
                        dangerouslySetInnerHTML={{
                            __html: "window.FPD_CONSENT = true;",
                        }}
                        type="text/plain"
                        data-purposes="analytics"
                        suppressHydrationWarning={true}
                    />
                    <script
                        dangerouslySetInnerHTML={{
                            __html: `window.ENV = ${JSON.stringify(data?.CLIENT_ENV)}`,
                        }}
                    />
                    <ModalProvider>{children}</ModalProvider>
                    <Toaster />
                    <ScrollRestoration />
                    <Scripts />
                    {GTM_ENABLED && <GoogleTagManagerFallback trackingId={GTM_ID} />}
                    <ClientOnly>{() => <Tracker />}</ClientOnly>
                </body>
            </html>
        </AuthenticityTokenProvider>
    );
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
    if (error && !isRouteErrorResponse(error) && error instanceof Error) {
        // you only want to capture non 404-errors that reach the error boundary
        Sentry.captureException(error);
    }
    const { title, description } = errorMeta(error);
    return (
        <PageContainer className="h-dvh">
            <ErrorContent title={title} description={description} />
        </PageContainer>
    );
}

export default function App() {
    return <Outlet />;
}
